import { ApiError } from "api/client";
import { addEntityTreeLink, createEntity, getTreeEntities, removeEntityTreeLink, updateEntity } from "api/entities";
import { addRelation, getEntityRelations, removeRelation } from "api/relations";
import type { CustomPropertyDto, EntityDto, EntityRelationDto, LinkedTreeDto, ReferenceLinkDto } from "api/types";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { flipRelationPerspective } from "utilities";

import CustomAttrRow from "./CustomAttrRow";
import { byEntityTitle, UNRESTRICTED_OPTIONS } from "./entityFormConfigs";
import type {
	CreatureFormState,
	CustomProp,
	EntityDialogProps,
	ReferenceLinkDraft,
	RelationDraft,
	RelationOption,
	TreeLinkDraft
} from "./entityForms.types";
import EntityImageGallery from "./EntityImageGallery";
import LineIcon from "./LineIcon";
import ReferenceLinkRow from "./ReferenceLinkRow";
import RelationChipItem from "./RelationChipItem";
import RelationTypePicker from "./RelationTypePicker";
import TreeLinkPicker from "./TreeLinkPicker";
import { useEntityImages } from "./useEntityImages";

import styles from "./formDialog.module.css";

// Every option reads "this creature is <type> of/in the target", so the forward edge is always From = this creature.
const ASSOCIATION_OPTIONS: RelationOption[] = [
	{ value: "BondedTo", targetTypes: ["Person"], canBeEx: true },
	{ value: "CompanionOf", targetTypes: ["Person"], canBeEx: true },
	// This creature is owned by the target person.
	{ value: "BelongsTo", targetTypes: ["Person"], canBeEx: true },
	{ value: "OriginatesFrom", targetTypes: ["Place", "Organization"] },
	{ value: "BelongsToPack", targetTypes: ["Organization"], canBeEx: true },
	{ value: "SeenIn", targetTypes: ["Place"] },
	// The API takes a Creature on the From side of Inhabits, but only the Species form offered it, so
	// a creature got the one-off "Seen In" and never a home range.
	{ value: "Inhabits", targetTypes: ["Place"] },
	{ value: "Enemy", targetTypes: ["Person", "Creature", "Organization"], canBeEx: true },
	...UNRESTRICTED_OPTIONS,
	{ value: "Other", targetTypes: ["Person", "Place", "Object", "Creature", "Organization"], canBeEx: true }
];

const CREATURE_TYPE_SUGGESTIONS = [
	"Direwolf",
	"Dragon",
	"Wolf",
	"Bear",
	"Horse",
	"Raven",
	"Griffin",
	"Serpent",
	"Spirit",
	"Familiar",
	"Mount",
	"Beast"
];

const ALIGNMENT_SUGGESTIONS = ["Companion", "Ally", "Neutral", "Wild", "Hostile"];

const newId = (): string =>
	typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const createInitialForm = (entity?: EntityDto | null): CreatureFormState => ({
	alignment: entity?.alignment ?? "",
	creatureType: entity?.creatureType ?? "",
	description: entity?.description ?? "",
	name: entity?.title ?? "",
	origin: entity?.origin ?? "",
	speciesName: entity?.speciesName ?? "",
	traits: entity?.traits ?? ""
});

const CreatureFormDialog = ({ entity, treeId, onClose, onCreated }: EntityDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const isEdit = Boolean(entity);

	const [form, setForm] = useState<CreatureFormState>(() => createInitialForm(entity));
	const gallery = useEntityImages({
		entityId: entity?.id ?? null,
		initialImages: entity?.images,
		getAccessToken
	});
	const [customProps, setCustomProps] = useState<CustomProp[]>(
		() =>
			entity?.customProperties?.map((prop: CustomPropertyDto) => ({
				id: prop.id,
				key: prop.key,
				value: prop.value ?? ""
			})) ?? []
	);
	const [referenceLinks, setReferenceLinks] = useState<ReferenceLinkDraft[]>(
		() => entity?.referenceLinks?.map((link: ReferenceLinkDto) => ({ id: link.id, url: link.url })) ?? []
	);
	const [treeLinks, setTreeLinks] = useState<TreeLinkDraft[]>(
		() =>
			entity?.linkedTrees?.map((link: LinkedTreeDto) => ({
				id: link.id,
				existingId: link.id,
				treeId: link.treeId,
				title: link.title
			})) ?? []
	);
	const [removedTreeLinkIds, setRemovedTreeLinkIds] = useState<string[]>([]);
	const [treeEntities, setTreeEntities] = useState<EntityDto[]>([]);
	const [associationType, setAssociationType] = useState<string>("");
	const [associationTarget, setAssociationTarget] = useState<string>("");
	const [associationIsEx, setAssociationIsEx] = useState(false);
	const [associations, setAssociations] = useState<RelationDraft[]>([]);
	const [removedAssociationIds, setRemovedAssociationIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");

	// Null until a type is picked, so an untouched dropdown can no longer record a relation
	// the author never chose. Everything downstream must cope with "nothing selected yet".
	const selectedOption = useMemo<RelationOption | null>(
		() => ASSOCIATION_OPTIONS.find((option: RelationOption) => option.value === associationType) ?? null,
		[associationType]
	);
	const availableTargets = useMemo<EntityDto[]>(
		() =>
			selectedOption
				? treeEntities
						.filter((item: EntityDto) => selectedOption.targetTypes.includes(item.type))
						.sort(byEntityTitle)
				: [],
		[selectedOption, treeEntities]
	);

	useEffect(() => {
		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getTreeEntities(treeId, token) : null))
			.then((list: EntityDto[] | null) => {
				if (active && list) {
					setTreeEntities(list.filter((item: EntityDto) => item.id !== entity?.id));
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity?.id, getAccessToken, treeId]);

	// Edit mode: show the creature's existing associations so they can be removed.
	useEffect(() => {
		if (!entity) {
			return undefined;
		}

		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getEntityRelations(entity.id, token) : null))
			.then((list: EntityRelationDto[] | null) => {
				if (active && list) {
					setAssociations(
						list.map((relation: EntityRelationDto) => ({
							id: relation.id,
							existingId: relation.id,
							relationType: flipRelationPerspective(relation.relationType),
							isEx: relation.isEx,
							targetId: relation.relatedEntityId,
							targetTitle: relation.relatedEntityTitle
						}))
					);
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity, getAccessToken]);

	const updateField = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
			const { name, value } = event.target;
			setForm((current: CreatureFormState) => ({ ...current, [name]: value }));
		},
		[]
	);

	const addCustomProp = useCallback((): void => {
		setCustomProps((current: CustomProp[]) => [...current, { id: newId(), key: "", value: "" }]);
	}, []);

	const updateCustomProp = useCallback((id: string, field: "key" | "value", value: string): void => {
		setCustomProps((current: CustomProp[]) =>
			current.map((prop: CustomProp) => (prop.id === id ? { ...prop, [field]: value } : prop))
		);
	}, []);

	const removeCustomProp = useCallback((id: string): void => {
		setCustomProps((current: CustomProp[]) => current.filter((prop: CustomProp) => prop.id !== id));
	}, []);

	const addReferenceLink = useCallback((): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) => [...current, { id: newId(), url: "" }]);
	}, []);

	const updateReferenceLink = useCallback((id: string, url: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.map((link: ReferenceLinkDraft) => (link.id === id ? { ...link, url } : link))
		);
	}, []);

	const removeReferenceLink = useCallback((id: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.filter((link: ReferenceLinkDraft) => link.id !== id)
		);
	}, []);

	const addTreeLink = useCallback((linkTreeId: string, title: string): void => {
		setTreeLinks((current: TreeLinkDraft[]) => [...current, { id: newId(), treeId: linkTreeId, title }]);
	}, []);

	const removeTreeLink = useCallback(
		(id: string): void => {
			const removed = treeLinks.find((link: TreeLinkDraft) => link.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedTreeLinkIds((ids: string[]) => [...ids, existingId]);
			}

			setTreeLinks((current: TreeLinkDraft[]) => current.filter((link: TreeLinkDraft) => link.id !== id));
		},
		[treeLinks]
	);

	const addRelationDraft = useCallback((): void => {
		if (!selectedOption || !associationTarget) {
			return;
		}

		const target = availableTargets.find((item: EntityDto) => item.id === associationTarget);
		if (!target) {
			return;
		}

		setAssociations((current: RelationDraft[]) => [
			...current,
			{
				id: newId(),
				relationType: associationType,
				isEx: Boolean(selectedOption.canBeEx) && associationIsEx,
				targetId: target.id,
				targetTitle: target.title
			}
		]);
		setAssociationTarget("");
		setAssociationIsEx(false);
	}, [associationIsEx, associationTarget, associationType, availableTargets, selectedOption]);

	const removeRelationDraft = useCallback(
		(id: string): void => {
			const removed = associations.find((association: RelationDraft) => association.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedAssociationIds((ids: string[]) => [...ids, existingId]);
			}

			setAssociations((current: RelationDraft[]) =>
				current.filter((association: RelationDraft) => association.id !== id)
			);
		},
		[associations]
	);

	const onAssociationTypeChange = useCallback((nextType: string): void => {
		setAssociationType(nextType);
		// The new type may allow a different set of targets, and may not support the ex modifier.
		setAssociationTarget("");
		setAssociationIsEx(false);
	}, []);

	const onAssociationTargetChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setAssociationTarget(event.target.value);
	}, []);

	const onAssociationIsExChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setAssociationIsEx(event.target.checked);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const name = form.name.trim();
			if (!name) {
				setError(t("editor.creatureForm.nameRequired"));

				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setError("");
			setIsSaving(true);

			try {
				const fields = {
					title: name,
					creatureType: form.creatureType.trim() || undefined,
					speciesName: form.speciesName.trim() || undefined,
					origin: form.origin.trim() || undefined,
					alignment: form.alignment.trim() || undefined,
					traits: form.traits.trim() || undefined,
					// Bonds/ownership are managed via associations; preserve any FK set elsewhere.
					ownerEntityId: entity?.ownerEntityId ?? undefined,
					description: form.description.trim() || undefined,
					customProperties: customProps
						.filter((prop: CustomProp) => prop.key.trim())
						.map((prop: CustomProp) => ({
							key: prop.key.trim(),
							value: prop.value.trim() || undefined
						})),
					referenceLinks: referenceLinks.map((link: ReferenceLinkDraft) => link.url.trim()).filter(Boolean)
				};

				const saved = entity
					? await updateEntity(entity.id, { ...fields, images: gallery.toInputPayload() }, token)
					: await createEntity(
							treeId,
							{ type: "Creature", ...fields, images: gallery.toInputPayload() },
							token
					  );

				await Promise.all(removedAssociationIds.map((relationId: string) => removeRelation(relationId, token)));

				// Every association reads "this creature is <type> of/in the target", so this creature is the From side.
				await Promise.all(
					associations
						.filter((association: RelationDraft) => !association.existingId)
						.map((association: RelationDraft) =>
							addRelation(
								{
									fromEntityId: saved.id,
									toEntityId: association.targetId,
									relationType: association.relationType,
									isEx: association.isEx
								},
								token
							)
						)
				);

				await Promise.all(removedTreeLinkIds.map((linkId: string) => removeEntityTreeLink(linkId, token)));
				await Promise.all(
					treeLinks
						.filter((link: TreeLinkDraft) => !link.existingId)
						.map((link: TreeLinkDraft) => addEntityTreeLink(saved.id, link.treeId, token))
				);

				onCreated(saved.id);
				onClose();
			} catch (submitError) {
				setError(submitError instanceof ApiError ? submitError.message : t("editor.creatureForm.saveError"));
			} finally {
				setIsSaving(false);
			}
		},
		[
			associations,
			customProps,
			entity,
			form,
			gallery,
			getAccessToken,
			onClose,
			onCreated,
			referenceLinks,
			removedAssociationIds,
			removedTreeLinkIds,
			t,
			treeId,
			treeLinks
		]
	);

	const hasTargets = availableTargets.length > 0;

	return (
		<div className={styles.personPanelOverlay}>
			<form
				ref={dialogRef}
				aria-labelledby="creature-form-title"
				aria-modal="true"
				className={styles.personFormPanel}
				onSubmit={onSubmit}
				role="dialog">
				<div className={styles.personFormHeader}>
					<div className={styles.personFormHeaderCopy}>
						<h1 id="creature-form-title">
							{isEdit ? t("editor.creatureForm.editTitle") : t("editor.creatureForm.addTitle")}
						</h1>
						<p>{t("editor.creatureForm.subtitle")}</p>
					</div>

					<button
						aria-label={t("editor.creatureForm.close")}
						className={styles.personFormCloseButton}
						onClick={onClose}
						type="button">
						<LineIcon name="close" />
					</button>
				</div>

				<div className={styles.personFormBody}>
					<div className={styles.personFormProfile}>
						<EntityImageGallery gallery={gallery} title={form.name} editable />
					</div>

					<div className={styles.personFormFields}>
						<h2>{t("editor.creatureForm.standardAttributes")}</h2>

						<div className={styles.personFormGrid}>
							<div className={styles.personFormField}>
								<label htmlFor="creature-name">
									{t("editor.creatureForm.name")} <span className={styles.requiredStar}>*</span>
								</label>
								<input
									id="creature-name"
									maxLength={fieldLimits.entityTitle}
									name="name"
									onChange={updateField}
									type="text"
									value={form.name}
									required
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="creature-type">{t("editor.creatureForm.type")}</label>
								<input
									id="creature-type"
									list="creature-type-suggestions"
									maxLength={fieldLimits.customPropertyValue}
									name="creatureType"
									onChange={updateField}
									type="text"
									value={form.creatureType}
								/>
								<datalist id="creature-type-suggestions">
									{CREATURE_TYPE_SUGGESTIONS.map((suggestion: string) => (
										<option key={suggestion} label={suggestion} value={suggestion} />
									))}
								</datalist>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="creature-species">{t("editor.creatureForm.species")}</label>
								<input
									id="creature-species"
									maxLength={fieldLimits.customPropertyValue}
									name="speciesName"
									onChange={updateField}
									type="text"
									value={form.speciesName}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="creature-origin">{t("editor.creatureForm.origin")}</label>
								<input
									id="creature-origin"
									maxLength={fieldLimits.customPropertyValue}
									name="origin"
									onChange={updateField}
									type="text"
									value={form.origin}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="creature-alignment">{t("editor.creatureForm.alignment")}</label>
								<input
									id="creature-alignment"
									list="creature-alignment-suggestions"
									maxLength={fieldLimits.customPropertyValue}
									name="alignment"
									onChange={updateField}
									type="text"
									value={form.alignment}
								/>
								<datalist id="creature-alignment-suggestions">
									{ALIGNMENT_SUGGESTIONS.map((suggestion: string) => (
										<option key={suggestion} label={suggestion} value={suggestion} />
									))}
								</datalist>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="creature-traits">{t("editor.creatureForm.traits")}</label>
								<input
									id="creature-traits"
									maxLength={fieldLimits.customPropertyValue}
									name="traits"
									onChange={updateField}
									type="text"
									value={form.traits}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="creature-description">{t("editor.creatureForm.description")}</label>
								<textarea
									id="creature-description"
									maxLength={fieldLimits.customPropertyValue}
									name="description"
									onChange={updateField}
									rows={4}
									value={form.description}
								/>
							</div>
						</div>
					</div>

					<section className={styles.personFormSection}>
						<h2>{t("editor.creatureForm.associations")}</h2>
						<div className={styles.relationAddRow}>
							<RelationTypePicker
								controlClassName={styles.relationSelect}
								disabled={isSaving}
								onChange={onAssociationTypeChange}
								options={ASSOCIATION_OPTIONS}
								value={associationType}
							/>
							<select
								aria-label={t("editor.creatureForm.associationTarget")}
								className={styles.relationSelect}
								disabled={!hasTargets || isSaving}
								onChange={onAssociationTargetChange}
								value={associationTarget}>
								<option value="">{t("editor.creatureForm.associationTargetPlaceholder")}</option>
								{availableTargets.map((target: EntityDto) => (
									<option key={target.id} value={target.id}>
										{target.type === "Person" ? target.title : `${target.title} (${target.type})`}
									</option>
								))}
							</select>

							{selectedOption?.canBeEx && (
								<label className={styles.relationExToggle} htmlFor="creature-association-is-ex">
									<input
										checked={associationIsEx}
										disabled={isSaving}
										id="creature-association-is-ex"
										onChange={onAssociationIsExChange}
										type="checkbox"
									/>
									<span>{t("editor.creatureForm.formerAssociation")}</span>
								</label>
							)}
							<button
								className={styles.relationAddButton}
								disabled={!associationType || !associationTarget || isSaving}
								onClick={addRelationDraft}
								type="button">
								<LineIcon name="plus" />
								<span>{t("editor.creatureForm.addAssociation")}</span>
							</button>
						</div>

						{!selectedOption && (
							<p className={styles.relationHint}>{t("editor.relationPicker.chooseTypeHint")}</p>
						)}
						{selectedOption && !hasTargets && (
							<p className={styles.relationHint}>{t("editor.creatureForm.noTargetsHint")}</p>
						)}

						{associations.length > 0 && (
							<div className={styles.relationChips}>
								{associations.map((association: RelationDraft) => (
									<RelationChipItem
										key={association.id}
										onRemove={removeRelationDraft}
										relation={association}
										removeLabel={t("editor.creatureForm.removeAssociation")}
									/>
								))}
							</div>
						)}
					</section>

					<TreeLinkPicker
						currentTreeId={treeId}
						links={treeLinks}
						onAdd={addTreeLink}
						onRemove={removeTreeLink}
					/>

					<section className={styles.personFormSection}>
						<h2>{t("editor.creatureForm.customAttributes")}</h2>
						{customProps.map((prop: CustomProp) => (
							<CustomAttrRow
								key={prop.id}
								idPrefix="creature"
								keyLabel={t("editor.creatureForm.key")}
								onChange={updateCustomProp}
								onRemove={removeCustomProp}
								prop={prop}
								removeLabel={t("editor.creatureForm.removeCustomAttribute")}
								valueLabel={t("editor.creatureForm.value")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addCustomProp} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.creatureForm.addCustomAttribute")}</span>
						</button>
					</section>

					<section className={styles.personFormSection}>
						<h2>{t("editor.creatureForm.references")}</h2>
						{referenceLinks.map((link: ReferenceLinkDraft) => (
							<ReferenceLinkRow
								key={link.id}
								link={link}
								onChange={updateReferenceLink}
								onRemove={removeReferenceLink}
								removeLabel={t("editor.creatureForm.removeReference")}
								urlLabel={t("editor.creatureForm.referenceUrl")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addReferenceLink} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.creatureForm.addReference")}</span>
						</button>
					</section>

					{error && <div className={styles.personFormError}>{error}</div>}
				</div>

				<div className={styles.personFormActions}>
					<button
						className={styles.personFormCancelButton}
						disabled={isSaving}
						onClick={onClose}
						type="button">
						{t("editor.creatureForm.cancel")}
					</button>
					<button className={styles.personFormSaveButton} disabled={isSaving} type="submit">
						{isSaving ? t("editor.creatureForm.saving") : t("editor.creatureForm.save")}
					</button>
				</div>
			</form>
		</div>
	);
};

export default CreatureFormDialog;
